import React, { useState, useEffect, useMemo } from 'react';
import TicketTable from '../TicketTable/TicketTable';
import TicketDetailsPanel from '../TicketDetailsPanel/TicketDetailsPanel';
import KPIsBar from '../KPIsBar/KPIsBar';
import { CommandCenterProvider, useCommandCenter } from '../utils/CommandCenterContext';
import { Ticket } from '../utils/commandCenterTypes';
import { redirectToSlugAwareLogin } from '../../../helpers/AuthRedirect';
import { getLoginUniqueId } from '../../../helpers/commandCentreRouting';
import { buildCoreAssetUrl, environment } from '../../../config/environment';
import { coreApi as axios } from '../../../api/coreApi';
import {
  CommandCentreInstance,
  CommandCentreInstanceUser,
  getInstanceByUniqueId,
  listInstanceUsers,
  listInstances,
} from '../../../api/instances';
import { useAuth } from '../../../auth/AuthProvider';
import { CommandCentreAuthContext, CommandCentreRoleBinding } from '../../../types/commandCentreAuth';
import '../CommandCenterHome/CommandCenterHome.css';
import Conversation from '../../Conversation/Conversation';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';
import { getTicket } from '../../../services/ticketService';
import { applyTicketKpiFilter, applyTicketScope, isTicketClosed } from '../utils/ticketFilters';
const DEFAULT_USER_AVATAR =
  'https://www.gravatar.com/avatar/?d=mp&s=200';

// Temporarily hidden while Businesses navigation is being reworked; direct routes remain available.
const SHOW_BUSINESSES_NAV = false;

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  email?: string;
  role?: string;
  profile_picture?: { url?: string } | string | null;
  profilePicture?: string | null;

  // Optional: role/activity fields if your API provides them
  instance_roles?: CommandCentreRoleBinding[] | string;
  last_active_at?: string | null;  // ISO string
  lastLoginAt?: string | null;     // ISO string
  updatedAt?: string | null;       // ISO string
}

interface InstanceBranding {
  logoUrl?: string | null;
  backgroundColor?: string | null;
  textColor?: string | null;
  [key: string]: any; // optional: allows extra fields if API returns more
}

interface CreateInstanceFormValues {
  instance_name: string;
  instance_email: string;
  instance_password: string;
  notify_emails?: string;
}

interface HostingInfo {
  websiteUrl?: string | null;
  status?: string | null;
}

interface CommandCenterCapabilities {
  tickets?: boolean;
  ticketAssignments?: boolean;
  ticketConversations?: boolean;
  ticketAttachments?: boolean;
  sprints?: boolean;
  userProvisioning?: boolean;
  businessProvisioning?: boolean;
  impersonation?: boolean;
  launch?: boolean;
}

// ---------- SPRINT TYPES ----------
interface SprintAssignment {
  task_id: number;
  assignee_type: 'user' | 'instance' | null;
  assignee_id: number | null;
}

interface SprintAssignedUserDetail {
  id: number;
  display_name: string;
  email_address?: string;
  profile_picture?: { url?: string } | string | null;
}

interface SprintTasksSummary {
  total: number;
  completed: number;
  open: number;
  by_status: Record<string, number>;
}

interface Sprint {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  status: string; // planned | active | completed
  task_assignments: SprintAssignment[];
  assigned_user_details?: SprintAssignedUserDetail[];
  tasks_summary?: SprintTasksSummary;
}

interface NewSprintState {
  name: string;
  start_date: string;
  end_date: string;
}

// —————————————————————————————————————————————————————
// Helpers: roles & presence
// —————————————————————————————————————————————————————
const getRoleBindings = (u: User) => {
  try {
    if (Array.isArray(u.instance_roles)) return u.instance_roles;
    if (typeof u.instance_roles === 'string') return JSON.parse(u.instance_roles);
  } catch {}
  return [];
};

const getRoleForInstance = (
  u: User,
  instanceId: number
): 'administrator' | 'user' | null => {
  const roles = getRoleBindings(u);
  const rb = roles.find((r: any) => Number(r?.instance_id) === Number(instanceId));
  if (!rb) return null;
  return rb.user_role === 'administrator' ? 'administrator' : 'user';
};

const isActiveWithinHours = (u: User, hours = 24) => {
  const ts = u.last_active_at || u.lastLoginAt || u.updatedAt || null;
  if (!ts) return false;
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= hours * 60 * 60 * 1000;
};

const CommandCenterContent: React.FC<{ commandCentreAuth: CommandCentreAuthContext | null; appLoading: any }> = ({
  commandCentreAuth,
  appLoading,
}) => {
  const [activeTab, setActiveTab] = useState('Tickets');

  const { logout } = useAuth();
  const isCoreSuperAdmin = commandCentreAuth?.isCoreSuperAdmin === true;

  const { tickets, fetchTickets, ticketRequestState, ticketRequestError } = useCommandCenter();
  const ticketsLoading =
    appLoading || ticketRequestState === 'idle' || ticketRequestState === 'loading';

  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [hostingModal, setHostingModal] = useState<{ show: boolean; data: HostingInfo | null }>({
    show: false,
    data: null,
  });

  const [instances, setInstances] = useState<CommandCentreInstance[]>([]);
  const [activeInstance, setActiveInstance] = useState<CommandCentreInstance | null>(null);
  const [serviceCapabilities, setServiceCapabilities] = useState<CommandCenterCapabilities>({});
  const [selectedTicketState, setSelectedTicketState] = useState<
    'idle' | 'loading' | 'loaded' | 'forbidden' | 'not-found' | 'unavailable' | 'error'
  >('idle');
  const [selectedTicketError, setSelectedTicketError] = useState<string | null>(null);

  const [instanceBranding, setInstanceBranding] = useState<InstanceBranding>({});
  const [selectedInstanceUsers, setSelectedInstanceUsers] = useState<CommandCentreInstanceUser[]>([]);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [isInternalUser, setIsInternalUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    first_name: '',
    last_name: '',
    email_address: '',
    user_role: 'user', // default for slug mode
  });

  const [showCreateInstanceModal, setShowCreateInstanceModal] = useState(false);
  const [scopedInstance, setScopedInstance] = useState<{ id: number; name: string; slug: string } | null>(null);
  const [filterKey, setFilterKey] = useState<string>('all');
  const [ticketScope, setTicketScope] = useState<'mine' | 'all'>('mine');
  const [instanceSearch, setInstanceSearch] = useState('');
  const [internalUsers, setInternalUsers] = useState<CommandCentreInstanceUser[]>([]);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);

  // ---------- SPRINT STATE ----------
  const [sprints] = useState<Sprint[]>([]);
  const [sprintsLoading] = useState<boolean>(false);
  const [showCreateSprintPanel, setShowCreateSprintPanel] = useState<boolean>(false);
  const [newSprint, setNewSprint] = useState<NewSprintState>({
    name: '',
    start_date: '',
    end_date: '',
  });
  const [newSprintSelectedTasks, setNewSprintSelectedTasks] = useState<number[]>([]);
  type NewSprintAssignee = {
    assignee_type: 'user' | 'instance';
    assignee_id: number;
  };

  // per task -> array of assignees (can be multiple)
  const [newSprintAssignments, setNewSprintAssignments] = useState<{
    [taskId: number]: NewSprintAssignee[];
  }>({});

  // NEW: sprint overview modal
  const [showSprintViewModal, setShowSprintViewModal] = useState(false);
  const [activeSprint, setActiveSprint] = useState<Sprint | null>(null);
  const fetchSprints = async () => {};

  const location = useLocation();

  // Login unique IDs
  const login_unique_id = getLoginUniqueId(location.pathname, location.search) || undefined;
  const hasSlug = Boolean(login_unique_id);

  const [recentlyUpdatedTicketId, setRecentlyUpdatedTicketId] = useState<number | null>(null);

  // When present, we’ll try to open this ticket on load
  const [deepLinkTaskId, setDeepLinkTaskId] = useState<number | null>(null);  

  // When any ticket gets activity (new conversation, status change, etc.)
  const handleTicketActivity = async (ticketId: number) => {
    setRecentlyUpdatedTicketId(ticketId);
  
    // 🔥 Refresh tickets so updatedAt is new
    await fetchTickets(hasSlug && scopedInstance?.id ? { instanceId: scopedInstance.id } : undefined);
  
    // remove highlight after 5s
    setTimeout(() => {
      setRecentlyUpdatedTicketId(null);
    }, 5000);
  };
  

  // Which users can be assigned to a new web task:
  // - internal view: all internal users
  // - slug view: users for this scoped business
  const assignableUsersForTickets = useMemo(
    () => (hasSlug ? selectedInstanceUsers : internalUsers),
    [hasSlug, selectedInstanceUsers, internalUsers]
  );


  const isInternalDashboard = !hasSlug;

  // 🔐 Scoped business admin on slug dashboards (elite-core administrator)
  const isScopedInstanceAdministrator =
    hasSlug && scopedInstance
      ? !!commandCentreAuth?.entity?.roles?.some(
          (role: any) =>
            Number(role?.instance_id) === Number(scopedInstance.id) &&
            role?.user_role === 'administrator'
        )
      : false;

  const canCreateUsers =
    serviceCapabilities.userProvisioning === true &&
    (isCoreSuperAdmin || isScopedInstanceAdministrator);
  const canCreateInstances = serviceCapabilities.businessProvisioning === true;
  const canImpersonateInstances = serviceCapabilities.impersonation === true;
  const canLaunchInstances = serviceCapabilities.launch === true;
  const canSeeSprints = false;
  const canManageSprints = false;

  const tabs = useMemo(
    () => {
      if (hasSlug) {
        return ['Tickets', 'Teams'];
      }
      return ['Tickets', ...(SHOW_BUSINESSES_NAV ? ['Businesses', 'Teams'] : []), ''];
    },
    [hasSlug]
  );

  // NEW: loading state + dots for slug-based branding
  const [brandingLoading, setBrandingLoading] = useState<boolean>(!!login_unique_id);
  const [loadingDots, setLoadingDots] = useState<string>('');

  // NEW: user menu / profile dropup state
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileDropup, setShowProfileDropup] = useState(false);
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  // Animate "Loading", "Loading.", "Loading..", "Loading..." while branding is loading
  useEffect(() => {
    if (!brandingLoading) {
      setLoadingDots('');
      return;
    }

    const sequence = ['', '.', '..', '...'];
    let index = 0;

    const interval = setInterval(() => {
      index = (index + 1) % sequence.length;
      setLoadingDots(sequence[index]);
    }, 400);

    return () => clearInterval(interval);
  }, [brandingLoading]);
  // Fetch instance branding if login_unique_id exists
  useEffect(() => {
    if (!login_unique_id) {
      setBrandingLoading(false);
      return;
    }

    setBrandingLoading(true);

    getInstanceByUniqueId(login_unique_id)
      .then(async (instance) => {
        if (!instance) {
          throw new Error('Instance not found');
        }

        setInstanceBranding({
          logoUrl: instance.logoUrl || null,
          backgroundColor: instance.backgroundColor || null,
          textColor: instance.textColor || null,
        });
        setScopedInstance({
          id: instance.id,
          name: instance.displayName,
          slug: login_unique_id,
        });

        const response = await listInstanceUsers(instance.id);
        setSelectedInstanceUsers(response.users);
      })
      .catch((err) => {
        console.error('Failed to fetch instance branding', err);
        setInstanceBranding({});
      })
      .finally(() => {
        setBrandingLoading(false);
      });
  }, [login_unique_id]);

  useEffect(() => {
    if (hasSlug && !scopedInstance?.id) return;
    fetchTickets(hasSlug && scopedInstance?.id ? { instanceId: scopedInstance.id } : undefined);
  }, [fetchTickets, hasSlug, scopedInstance?.id]);

  // Fetch instances & internal users
  useEffect(() => {
    async function fetchCapabilities() {
      try {
        const res = await axios.get('/command-center/');
        setServiceCapabilities(res.data?.capabilities || {});
      } catch (err) {
        console.error('Failed to fetch Command Centre capabilities', err);
      }
    }

    async function fetchAccessibleInstances() {
      try {
        setInstances(await listInstances());
      } catch (err) {
        console.error('Failed to fetch instances', err);
      }
    }
    fetchCapabilities();
    fetchAccessibleInstances();

    async function fetchInternalUsers() {
      try {
        const res = await axios.get(
          environment.coreServerUrl + '/command-center/internal-users',
          { withCredentials: true }
        );
        setInternalUsers(res.data.users || []);
      } catch (err) {
        console.error('Failed to fetch internal users', err);
      }
    }
    fetchInternalUsers();
  }, []);

  const handleImpersonate = async (instanceId: number) => {
    const selected = instances.find((instance) => instance.id === instanceId) || null;
    setActiveInstance(selected);

    try {
      await axios.post(
environment.coreServerUrl + '/command-center/impersonate',
        { instanceId },
        { withCredentials: true }
      );
    } catch (err) {
      console.error('Impersonation failed:', err);
    }
  };

  const fetchUsersForInstance = async (instanceId: number) => {
    try {
      const response = await listInstanceUsers(instanceId);
      setSelectedInstanceUsers(response.users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setSelectedInstanceUsers([]);
    }
  };

  const handleOpenCreateUserModal = (instance?: CommandCentreInstance) => {
    if (hasSlug && scopedInstance) {
      setActiveInstance({
        id: scopedInstance.id,
        instanceUniqueId: scopedInstance.slug,
        displayName: scopedInstance.name,
        email: '',
        hosting: null,
      });
      fetchUsersForInstance(scopedInstance.id);
      setIsInternalUser(false);
    } else {
      setActiveInstance(instance || null);
      if (instance) {
        fetchUsersForInstance(instance.id);
        setIsInternalUser(false);
      } else {
        setSelectedInstanceUsers([]);
        setIsInternalUser(true);
      }
    }
    setShowCreateUserModal(true);
  };

  const handleCreateUser = async () => {
    try {
      // 🔹 Priority:
      //  1) If we're on a slug URL, use the business from retrieve-details
      //  2) Otherwise, if not internal, use impersonated business (Businesses tab)
      const effectiveInstanceId =
        hasSlug && scopedInstance
          ? scopedInstance.id
          : isInternalUser
          ? undefined
          : activeInstance?.id;

      const effectiveInstanceName =
        hasSlug && scopedInstance
          ? scopedInstance.name
          : activeInstance?.displayName;

      const payload: any = {
        ...newUserData,
        instanceId: effectiveInstanceId,
        instanceDisplayName: effectiveInstanceName,
        instanceUniqueId: login_unique_id || undefined,
      };

      const res = await axios.post(
environment.coreServerUrl + '/command-center/create-new-user',
        payload,
        { withCredentials: true }
      );
      console.log('New user created:', res.data);

      if (!isInternalUser && effectiveInstanceId) {
        fetchUsersForInstance(effectiveInstanceId);
      }

      setShowCreateUserModal(false);
      setNewUserData({
        first_name: '',
        last_name: '',
        email_address: '',
        user_role: 'user',
      });
    } catch (err: any) {
      console.error('Failed to create user:', err?.response?.data || err);
    }
  };

  const handleOpenCreateBusinessModal = () => {
    setShowCreateInstanceModal(true);
  };

  const handleCreateInstance = async (
    values: CreateInstanceFormValues,
    helpers: FormikHelpers<CreateInstanceFormValues>
  ) => {
    try {
      const additional_emails = values.notify_emails
        ? values.notify_emails.split(',').map((e) => e.trim()).filter(Boolean)
        : [];

      const res = await axios.post(
        `${environment.coreServerUrl}/command-center/create-business`,
        {
          business_name: values.instance_name,
          business_email: values.instance_email,
          business_password: values.instance_password,
          additional_emails,
        },
        { withCredentials: true }
      );

      setInstances((prev) => [...prev, res.data.business]);
      setShowCreateInstanceModal(false);
    } catch (err: any) {
      console.error('Failed to create instance:', err);
      helpers.setFieldError(
        'instance_email',
        err?.response?.data?.errorMessage || 'Failed to create business'
      );
      helpers.setSubmitting(false);
    }
  };

  async function retrieveUserStep(userId: number) {
    try {
      const res = await axios.get(
`${environment.coreServerUrl}/user-management/retrieve-details`,
        {
          params: { user_id: userId },
          withCredentials: true,
        }
      );
      if (res.data) {
        setUserDetails(res.data);
        if (res.data.profile_picture) {
          setProfilePicture(
            res.data.profile_picture.startsWith('http')
              ? res.data.profile_picture
              : buildCoreAssetUrl(res.data.profile_picture)
          );
        }
      }
    } catch (err) {
      console.error('Failed to fetch user details', err);
    }
  }

  useEffect(() => {
    if (commandCentreAuth?.entity?.type === 'user' && commandCentreAuth?.entity?.id) {
      retrieveUserStep(commandCentreAuth.entity?.id);
    }
  }, [commandCentreAuth?.entity?.type, commandCentreAuth?.entity?.id]);

  useEffect(() => {
    // Example: /dashboard/command-center/elite-core/?task=123
    const searchParams = new URLSearchParams(location.search);
    const taskParam = searchParams.get('task');
  
    if (taskParam) {
      const parsed = parseInt(taskParam, 10);
      if (!Number.isNaN(parsed)) {
        setDeepLinkTaskId(parsed);
      }
    }
  }, [location.search]);
  

  const loadFullTicket = async (ticketId: number) => {
    setSelectedTicketState('loading');
    setSelectedTicketError(null);
    setShowModal(true);

    try {
      const ticket = await getTicket(ticketId);
      setSelectedTicket(ticket);
      setSelectedTicketState('loaded');
      if (ticket.instanceId) {
        await fetchUsersForInstance(ticket.instanceId);
      }
      return ticket;
    } catch (error: any) {
      const message = error?.message || 'Failed to load the full ticket details.';
      setSelectedTicket(null);
      setSelectedTicketError(message);
      if (error?.kind === 'forbidden') {
        setSelectedTicketState('forbidden');
      } else if (error?.kind === 'not-found') {
        setSelectedTicketState('not-found');
      } else if (error?.kind === 'unavailable') {
        setSelectedTicketState('unavailable');
      } else {
        setSelectedTicketState('error');
      }
      return null;
    }
  };

  const handleRowClick = async (ticket: Ticket) => {
    await loadFullTicket(ticket.id);
  };

  const handleOpenHostingModal = (hosting: HostingInfo) => {
    setHostingModal({ show: true, data: hosting });
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
    setSelectedTicketState('idle');
    setSelectedTicketError(null);
  };

  // 🔹 Tickets scoped by slug (business) when applicable
  const effectiveTickets = useMemo(() => {
    // Internal dashboard: see everything
    if (!hasSlug || !scopedInstance?.id) {
      return tickets;
    }

    // Slug dashboard: only tickets for this business sender
    return tickets.filter((ticket: any) => {
      const sender = ticket.sender || (ticket as any).sender_info;
      if (!sender) return false;
      if (sender.type !== 'instance') return false;
      return Number(sender.id) === Number(scopedInstance.id);
    });
  }, [tickets, hasSlug, scopedInstance?.id]);

  // 🔗 If URL has ?task=ID, auto-open that ticket once tickets are loaded
  useEffect(() => {
    if (!deepLinkTaskId) return;
    if (!effectiveTickets || effectiveTickets.length === 0) return;

    const match = effectiveTickets.find(
      (t: any) => Number(t.id) === Number(deepLinkTaskId)
    );

    if (!match) return;

    // Make sure we’re on the Tickets tab
    setActiveTab('Tickets');

    loadFullTicket(Number(deepLinkTaskId));

    // Prevent reopening if something re-renders
    setDeepLinkTaskId(null);
    // loadFullTicket is intentionally excluded to avoid re-triggering on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTaskId, effectiveTickets]);

  const filteredTickets = useMemo(() => {
    if (!effectiveTickets) return [];

    const scopedTickets = applyTicketScope(
      effectiveTickets as Ticket[],
      ticketScope,
      commandCentreAuth
    );

    return applyTicketKpiFilter(scopedTickets, filterKey, ticketScope) as Ticket[];
  }, [effectiveTickets, ticketScope, filterKey, commandCentreAuth]);



  const filteredInstances = instances.filter((b) => {
    const createdAtStr = b.createdAt;

    if (filterKey === 'hosting') {
      return b.hosting && Object.keys(b.hosting).length > 0;
    }

    if (!createdAtStr) return false;

    const createdAt = new Date(createdAtStr.replace(' ', 'T')).getTime();
    if (isNaN(createdAt)) return false;

    switch (filterKey) {
      case '24h':
        return Date.now() - createdAt <= 24 * 60 * 60 * 1000;
      case '7d':
        return Date.now() - createdAt <= 7 * 24 * 60 * 60 * 1000;
      case '30d':
        return Date.now() - createdAt <= 30 * 24 * 60 * 60 * 1000;
      case 'all':
      default:
        return true;
    }
  });

  // 🔹 Logout handler (uses your endpoint)
  const handleLogout = () => {
    logout();
    redirectToSlugAwareLogin();
  };

  const handleSaveProfilePicture = async () => {
    if (!profileFile || !commandCentreAuth?.entity?.id) return;

    setProfileSaving(true);
    setProfileError(null);

    try {
      const formData = new FormData();
      formData.append('profile_picture', profileFile);
      formData.append('user_id', String(commandCentreAuth.entity.id));

      const res = await axios.post(
`${environment.coreServerUrl}/user-management/update-profile-picture`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const newUrl = res.data?.profile_picture_url || res.data?.profile_picture || null;
      if (newUrl) {
        setProfilePicture(
          newUrl.startsWith('http')
            ? newUrl
            : buildCoreAssetUrl(newUrl)
        );
      }

      setShowProfileDropup(false);
      setShowUserMenu(false);
      setProfileFile(null);
    } catch (err: any) {
      console.error('Failed to save profile picture', err);
      setProfileError(
        err?.response?.data?.errorMessage || 'Failed to save profile picture.'
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setProfileFile(e.target.files[0]);
      setProfileError(null);
    }
  };

  // ---------- SPRINT HELPERS ----------

  // For ticket selection:
  // - internal view: global tickets
  // - slug view: business-scoped via effectiveTickets
  const openTicketsForSprints = useMemo(
    () => effectiveTickets.filter((t) => !isTicketClosed(t.task_status)),
    [effectiveTickets]
  );

  // Assignment pools:
  // - internal view: all internal users + all instances
  // - slug view: users from this business eco-base + that business only
  const assignableUsers: User[] = hasSlug ? selectedInstanceUsers : internalUsers;
  const assignableInstances: CommandCentreInstance[] = hasSlug && scopedInstance
    ? ([
        {
          id: scopedInstance.id,
          displayName: scopedInstance.name,
          instanceUniqueId: scopedInstance.slug,
          email: '',
          hosting: null,
        },
      ] as CommandCentreInstance[])
    : instances;

  const toggleSprintTaskSelected = (taskId: number) => {
    setNewSprintSelectedTasks((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const setAssignmentsForTaskFromMulti = (
    taskId: number,
    selectedOptions: HTMLOptionsCollection
  ) => {
    const assignees: NewSprintAssignee[] = [];

    Array.from(selectedOptions).forEach((opt) => {
      const value = opt.value;
      if (!value) return;

      const [assignee_type, idStr] = value.split(':');
      const idNum = parseInt(idStr, 10);

      if (
        (assignee_type === 'user' || assignee_type === 'instance') &&
        Number.isFinite(idNum)
      ) {
        assignees.push({
          assignee_type: assignee_type as 'user' | 'instance',
          assignee_id: idNum,
        });
      }
    });

    setNewSprintAssignments((prev) => {
      const next = { ...prev };
      if (assignees.length === 0) {
        delete next[taskId];
      } else {
        next[taskId] = assignees;
      }
      return next;
    });
  };

  const handleCreateSprintSubmit = async () => {
    try {
      if (!newSprint.name || !newSprint.start_date || !newSprint.end_date) {
        alert('Please provide a sprint name, start date, and end date.');
        return;
      }

      // Build one assignment row per (task, assignee) pairing.
      // If a task is selected but has no assignees, we still send a row with just task_id.
      // Build one assignment row per (task, assignee) pairing.
      // If a task is selected but has no assignees, we still send a row with just task_id.
      const assignments: SprintAssignment[] = [];

      newSprintSelectedTasks.forEach((taskId) => {
        const assigneesForTask = newSprintAssignments[taskId] || [];

        if (assigneesForTask.length === 0) {
          // task in sprint, but unassigned
          assignments.push({
            task_id: taskId,
            assignee_type: null,
            assignee_id: null,
          });
        } else {
          assigneesForTask.forEach((a) => {
            assignments.push({
              task_id: taskId,
              assignee_type: a.assignee_type,
              assignee_id: a.assignee_id,
            });
          });
        }
      });

      const cleanedAssignments = assignments.filter((a) =>
        Number.isFinite(a.task_id)
      );

      const payload: any = {
        name: newSprint.name,
        start_date: newSprint.start_date,
        end_date: newSprint.end_date,
        assignments: cleanedAssignments,
      };

      // If slug-based, tag sprint with this instanceId so backend scopes it
      if (hasSlug && scopedInstance?.id) {
        payload.instanceId = scopedInstance.id;
      }

      await axios.post(
environment.coreServerUrl + '/command-center/sprints',
        payload,
        { withCredentials: true }
      );

      await fetchSprints();

      setShowCreateSprintPanel(false);
      setNewSprint({
        name: '',
        start_date: '',
        end_date: '',
      });
      setNewSprintSelectedTasks([]);
      setNewSprintAssignments({});
    } catch (err: any) {
      console.error('Failed to create sprint', err?.response?.data || err);
      alert('Failed to create sprint.');
    }
  };

  const handleViewSprint = (sprint: Sprint) => {
    setActiveSprint(sprint);
    setShowSprintViewModal(true);
  };

  const handlePreviewSprint = async (sprintId: number) => {
    try {
      // 🔹 tenant slug for SES (elite-core, etc.)
      const tenantSlug = login_unique_id || scopedInstance?.slug || undefined;
      const body = tenantSlug ? { slug: tenantSlug } : {};
  
      await axios.post(
`${environment.coreServerUrl}/command-center/sprints/${sprintId}/preview-notify`,
        body, // 🔹 now carries { slug: 'elite-core' } on slug-based URLs
        { withCredentials: true }
      );
      alert('Preview email sent to internal recipient.');
    } catch (err: any) {
      console.error('Failed to send sprint preview email', err?.response?.data || err);
      alert('Failed to send preview email.');
    }
  };
  

  const handleNotifySprint = async (sprintId: number) => {
    try {
      // 🔹 tenant slug for SES (elite-core, etc.)
      const tenantSlug = login_unique_id || scopedInstance?.slug || undefined;
      const body = tenantSlug ? { slug: tenantSlug } : {};
  
      await axios.post(
`${environment.coreServerUrl}/command-center/sprints/${sprintId}/notify-users`,
        body, // 🔹 now carries { slug: 'elite-core' } on slug-based URLs
        { withCredentials: true }
      );
      alert('Sprint notifications sent.');
      await fetchSprints();
    } catch (err: any) {
      console.error('Failed to notify sprint assignees', err?.response?.data || err);
      alert('Failed to notify sprint assignees.');
    }
  }; 

  const handleLaunchCommandCenter = async (instanceId: any) => {
    try {
      const res = await axios.post(
        environment.coreServerUrl + '/command-center/launch',
        { instanceId },
        { withCredentials: true }
      );
  
      const slug = res.data.slug;
  
      alert("Command Center launched for slug: " + slug);
  
      // Auto-open login page (optional)
      window.open(`/dashboard/login/${slug}`, "_blank");
    } catch (err) {
      console.error(err);
      alert("Failed to launch command center.");
    }
  };  

// People we can show in conversation + @mentions
// - slug dashboards: business users + the business itself
// - internal dashboards: internal users
const conversationParticipants = useMemo(() => {
  const userSource: User[] = hasSlug ? selectedInstanceUsers : internalUsers;

  const normalizeAvatar = (u: User): string => {
    if (!u.profile_picture) return "";
    if (typeof u.profile_picture === "string") return u.profile_picture;
    return (u.profile_picture as any).url || "";
  };

  const users = userSource.map((u) => ({
    id: u.id,
    type: "user",
    display_name:
      `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
      u.email_address ||
      `User #${u.id}`,
    email: u.email_address,
    // Conversation expects a string path; backend prepends server URL
    profile_picture: normalizeAvatar(u),
  }));

  const instanceEntries: any[] = [];

  // On slug dashboards, also allow the instance itself as a sender/mention
  if (hasSlug && scopedInstance) {
    instanceEntries.push({
      id: scopedInstance.id,
      type: "instance",
      display_name: scopedInstance.name,
      email: "",

      // Optional: show the instance logo as avatar in conversation
      profile_picture: instanceBranding?.logoUrl || "",
    });
  }

  return [...users, ...instanceEntries];
}, [hasSlug, selectedInstanceUsers, internalUsers, scopedInstance, instanceBranding?.logoUrl]);

  
  // 🔐 Internal Command Center (no slug) is *only* for core-command-administrator
  if (false && isInternalDashboard && !isCoreSuperAdmin) {
    return (
      <div className="command-center-loading-screen">
        <div className="command-center-permission-denied">
          <div className="cc-permission-card">
            <i className="fa fa-shield-alt cc-permission-icon" />
            <h2>Permission denied</h2>
            <p>
              You don&apos;t have access to the following Command Center.
              Please use your business dashboard link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 🔹 If we're on a slug-based URL and branding is still loading, show the loading screen
  if (login_unique_id && brandingLoading) {
    return (
      <div className="command-center-loading-screen">
        <div className="sk-chase">
          <div className="sk-chase-dot"></div>
          <div className="sk-chase-dot"></div>
          <div className="sk-chase-dot"></div>
          <div className="sk-chase-dot"></div>
          <div className="sk-chase-dot"></div>
          <div className="sk-chase-dot"></div>
        </div>
        <div className="command-center-loading-text" style={{ marginTop: '16px' }}>
          Loading{loadingDots}
        </div>
      </div>
    );
  }

  return (
    <div className="command-center-branded">
      <KPIsBar
        filterKey={filterKey}
        setFilterKey={setFilterKey}
        activeTab={activeTab}
        instances={instances}
        onCreateInstance={canCreateInstances ? handleOpenCreateBusinessModal : undefined}
        assignableUsers={assignableUsersForTickets}
        onTicketActivity={handleTicketActivity}
        externalTicketScope={ticketScope}             
        setExternalTicketScope={setTicketScope}
        commandCentreAuth={commandCentreAuth}
      />

      <div className="command-center-main">
        <div className="command-center-section-controls">
          <div className="cc-tabs" aria-label="Ticket module navigation">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`cc-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
                style={{
                  background:
                    activeTab === tab ? instanceBranding?.backgroundColor || undefined : undefined,
                  color: activeTab === tab ? '#fff' : undefined,
                }}
              >
                {tab}
              </button>
            ))}
            {activeTab === 'Teams' && canCreateUsers && (
              <button
                className="create-internal-user-btn"
                onClick={() => handleOpenCreateUserModal()}
              >
                <i className="fa fa-user-shield"></i>
              </button>
            )}
          </div>

          <div
            className="cc-user-profile"
            onMouseEnter={() => setShowUserMenu(true)}
            onMouseLeave={() => {
              setShowUserMenu(false);
              setShowProfileDropup(false);
            }}
          >
            {profilePicture ? (
              <img
                src={profilePicture}
                alt={userDetails?.first_name}
                className="cc-user-avatar"
              />
            ) : (
              <div className="cc-user-avatar">
                {commandCentreAuth?.name
                  ? commandCentreAuth.name
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')
                  : 'B'}
              </div>
            )}
            <span className="cc-user-name">{commandCentreAuth?.name || 'Command Centre User'}</span>

            {showUserMenu && (
              <div className="cc-user-menu">
                <button type="button" onClick={handleLogout}>
                  <i className="fa fa-sign-out-alt" /> Logout
                </button>
              </div>
            )}

            {showProfileDropup && (
              <div className="cc-profile-dropup">
                <h4>Update Profile Picture</h4>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfileFileChange}
                />
                {profileError && (
                  <div className="cc-profile-error">{profileError}</div>
                )}
                <div className="cc-profile-actions">
                  <button
                    type="button"
                    className="cc-profile-save-btn"
                    onClick={handleSaveProfilePicture}
                    disabled={!profileFile || profileSaving}
                  >
                    {profileSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {activeTab === 'Tickets' && ticketRequestState === 'unavailable' && (
          <div className="dashboard-message">
            <h3>Ticket service unavailable</h3>
            <p>{ticketRequestError?.message || 'The Command Centre ticket service has not been connected to Elite Core yet.'}</p>
          </div>
        )}

        {activeTab === 'Tickets' && ticketRequestState === 'unauthorized' && (
          <div className="dashboard-message">
            <h3>Session expired</h3>
            <p>Please sign in again to load Command Centre tickets.</p>
          </div>
        )}

        {activeTab === 'Tickets' && ticketRequestState === 'error' && (
          <div className="dashboard-message">
            <h3>Unable to load tickets</h3>
            <p>{ticketRequestError?.message || 'Command Centre could not load tickets from Elite Core.'}</p>
          </div>
        )}

        {activeTab === 'Tickets' && ticketRequestState !== 'unavailable' && ticketRequestState !== 'unauthorized' && ticketRequestState !== 'error' && (
          <TicketTable
            tickets={filteredTickets}
            onSelectTicket={handleRowClick}
            headerBgColor={instanceBranding?.backgroundColor || undefined}
            loading={ticketsLoading}
            recentlyUpdatedTicketId={recentlyUpdatedTicketId}
          />
        )}

        {false && activeTab === 'Sprints' && canSeeSprints && (
          <div className="sprints-tab">
            <div className="sprints-header">
              <h2>Sprints</h2>
              {canManageSprints && (
                <button
                  className="sprint-create-btn"
                  onClick={() => setShowCreateSprintPanel(true)}
                >
                  Create Sprint
                </button>
              )}
            </div>

            {sprintsLoading ? (
              <div className="empty-hint">Loading sprints...</div>
            ) : sprints.length === 0 ? (
              <div className="empty-hint">No sprints yet. Create your first sprint.</div>
            ) : (
              <div className="sprints-list">
                {sprints.map((s) => (
                  <div key={s.id} className="sprint-card">
                    <div className="sprint-card-header">
                      <h3>{s.name}</h3>
                      <span className={`sprint-status sprint-status-${s.status}`}>
                        {s.status}
                      </span>
                    </div>
                    <div className="sprint-dates">
                      {s.start_date} → {s.end_date}
                    </div>

                    <div className="sprint-summary-row">
                      {s.tasks_summary ? (
                        <>
                          <div>
                            <strong>Total:</strong> {s.tasks_summary.total}
                          </div>
                          <div>
                            <strong>Completed:</strong> {s.tasks_summary.completed}
                          </div>
                          <div>
                            <strong>Open:</strong> {s.tasks_summary.open}
                          </div>
                        </>
                      ) : (
                        <div>No tasks attached yet.</div>
                      )}
                    </div>

                    {s.assigned_user_details && s.assigned_user_details.length > 0 && (
                      <div className="sprint-assignees">
                        <strong>Users in sprint:</strong>{' '}
                        {s.assigned_user_details.map((u, idx) => (
                          <span key={u.id}>
                            {u.display_name}
                            {idx < s.assigned_user_details!.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="sprint-actions">
                      <button
                        className="sprint-view-btn"
                        onClick={() => handleViewSprint(s)}
                      >
                        View
                      </button>

                      {canManageSprints && (
                        <>
                          <button
                            className="sprint-preview-btn"
                            onClick={() => handlePreviewSprint(s.id)}
                          >
                            Send Preview
                          </button>
                          <button
                            className="sprint-notify-btn"
                            onClick={() => handleNotifySprint(s.id)}
                          >
                            Notify Assignees
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'Businesses' && (
          <>
            <div className="business-search-bar">
              <input
                type="text"
                placeholder="Search instances..."
                value={instanceSearch}
                onChange={(e) => setInstanceSearch(e.target.value)}
              />
            </div>

            <div className="business-flex-container">
              {filteredInstances
                .filter((b) => {
                  if (!instanceSearch) return true;
                  return b.displayName.toLowerCase().includes(instanceSearch.toLowerCase());
                })
                .map((b) => (
                  <div key={b.id} className="business-tile">
                    <div className="business-tile-inner">
                      <div className="business-avatar">
                        {b.logoUrl ? <img src={buildCoreAssetUrl(b.logoUrl)} alt="" /> : null}
                      </div>

                      <div className="business-info">
                        <h3>{b.displayName}</h3>
                        <p>{b.email}</p>
                      </div>

                      <div className="business-actions">
                        {b.hosting && (
                          <button
                            className="icon-btn"
                            onClick={() => handleOpenHostingModal(b.hosting!)}
                            title="Hosting Details"
                          >
                            <i className="fa fa-desktop"></i>
                          </button>
                        )}

                        {canImpersonateInstances ? (
                          <button
                            className="icon-btn"
                            onClick={() => handleImpersonate(b.id)}
                            title="Impersonate"
                          >
                            <i className="fa fa-user-secret"></i>
                          </button>
                        ) : null}
                        <button
                          className="icon-btn"
                          onClick={() => handleOpenCreateUserModal(b)}
                          title="View Details"
                        >
                          <i className="fa fa-eye"></i>
                        </button>

                        {canLaunchInstances ? (
                          <button
                            className="icon-btn launch-cc-btn"
                            onClick={() => handleLaunchCommandCenter(b.id)}
                            title="Launch Command Center"
                          >
                            <i className="fa fa-rocket"></i>
                          </button>
                        ) : null}

                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}

        {activeTab === 'Teams' && (
          <div className="teams-tab">
            {hasSlug ? (
              <>
                {/* Online Admins (Last 24h) */}
                {/* All Users in Selected Client */}
                <div className="team-section">
                  <h2>All Users — {scopedInstance?.name}</h2>

                  <div className="team-users-grid">
                    {selectedInstanceUsers
                      .slice()
                      .sort((a, b) => {
                        if (!scopedInstance) return 0;
                        const ra =
                          getRoleForInstance(a, scopedInstance.id) === 'administrator'
                            ? 0
                            : 1;
                        const rb =
                          getRoleForInstance(b, scopedInstance.id) === 'administrator'
                            ? 0
                            : 1;
                        if (ra !== rb) return ra - rb;
                        const an = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
                        const bn = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
                        return an.localeCompare(bn);
                      })
                      .map((u) => {
                        const role = scopedInstance
                          ? getRoleForInstance(u, scopedInstance.id) || 'user'
                          : 'user';
                        const active = isActiveWithinHours(u, 24);

                        // avatar src with CDN fallback
                        const avatarSrc = u.profile_picture
                          ? typeof u.profile_picture === 'string'
                            ? u.profile_picture
                            : (u.profile_picture as any)?.url || DEFAULT_USER_AVATAR
                          : DEFAULT_USER_AVATAR;

                        const initials =
                          `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.trim() ||
                          (u.email_address?.[0] || 'U');

                        return (
                          <div key={u.id} className="team-user-card">
                            <div className="team-user-avatar-wrapper">
                              {avatarSrc ? (
                                <img
                                  src={avatarSrc}
                                  alt={`${u.first_name} ${u.last_name}`}
                                  className="team-user-avatar-img"
                                />
                              ) : (
                                <div className="team-user-avatar-fallback">
                                  {initials.toUpperCase()}
                                </div>
                              )}
                              <span
                                className={`team-user-status-dot ${
                                  active ? 'online' : 'offline'
                                }`}
                                title={active ? 'Active in last 24h' : 'Inactive'}
                              />
                            </div>

                            <div className="team-user-main">
                              <div className="team-user-name-row">
                                <span className="team-user-name">
                                  {u.first_name} {u.last_name}
                                </span>
                                <span
                                  className={`team-user-role-pill ${
                                    role === 'administrator' ? 'admin' : 'user'
                                  }`}
                                >
                                  {role === 'administrator' ? 'Admin' : 'User'}
                                </span>
                              </div>

                              <div className="team-user-email" title={u.email_address}>
                                {u.email_address}
                              </div>

                              <div className="team-user-meta-row">
                                <span className="team-user-id">ID #{u.id}</span>
                                <span className="team-user-activity-label">
                                  {active ? 'Online' : 'Offline'}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                    {selectedInstanceUsers.length === 0 && (
                      <div className="empty-hint">No users found for this business yet.</div>
                    )}
                  </div>
                </div>

              </>
            ) : (
              <>
                {/* Internal (no slug) view remains the same */}
                <div className="internal-users-sidebar">
                  {internalUsers.map((u) => (
                    <div key={u.id} className="internal-user">
                      {u.profile_picture ? (
                        <img
                          src={(u.profile_picture as any)?.url || ''}
                          alt={u.first_name}
                          className="internal-user-avatar"
                        />
                      ) : (
                        <div className="internal-user-initials">
                          {u.first_name?.[0]}
                          {u.last_name?.[0]}
                        </div>
                      )}
                      <span>
                        {u.first_name} {u.last_name}
                      </span>
                    </div>
                  ))}
                </div>

                <Conversation
                  commandCentreAuth={commandCentreAuth}
                  appLoading={appLoading}
                  conversationData={{ conversation: [] }}
                  entityDisplayData={internalUsers}
                  model="internal-chat"
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* Sprint Overview Modal */}
      {false && showSprintViewModal && activeSprint && (
        <div
          className="ticket-modal-overlay"
          onClick={() => {
            setShowSprintViewModal(false);
            setActiveSprint(null);
          }}
        >
          <div
            className="ticket-modal-content sprint-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-modal-btn"
              onClick={() => {
                setShowSprintViewModal(false);
                setActiveSprint(null);
              }}
            >
              <i className="fa fa-times" />
            </button>

            {(() => {
  // --- Build per-assignee sections from sprint.task_assignments + tickets + ticket.assignees ---

  const assignments = activeSprint!.task_assignments || [];

  // Normalize ticket IDs to strings so numbers/strings still match
  const allTicketsById = new Map<string, Ticket>();
  effectiveTickets.forEach((t) => {
    const rawId = (t as any).id;
    if (rawId === undefined || rawId === null) return;
    const key = String(rawId);
    allTicketsById.set(key, t);
  });

  type Section = {
    key: string;
    assignee_type: 'user' | 'instance';
    assignee_id: number;
    label: string;
    email?: string;
    avatarUrl?: string | null;
    initials: string;
    isUnassigned?: boolean;
    tasks: Ticket[];
  };

  const sectionsMap = new Map<string, Section>();

  const allUsers: User[] = [...internalUsers, ...selectedInstanceUsers];

  // Track which task IDs are in this sprint, and which have explicit sprint-level assignees
  const sprintTaskIds = new Set<string>();
  const sprintAssignedTaskIds = new Set<string>();

  const getOrCreateSection = (key: string, base: Partial<Section>): Section => {
    if (!sectionsMap.has(key)) {
      sectionsMap.set(key, {
        key,
        assignee_type: (base.assignee_type as 'user' | 'instance') || 'user',
        assignee_id: base.assignee_id ?? -1,
        label: base.label || 'Unassigned tasks',
        email: base.email,
        avatarUrl: base.avatarUrl ?? null,
        initials: base.initials || '?',
        isUnassigned: base.isUnassigned || false,
        tasks: [],
      });
    }
    return sectionsMap.get(key)!;
  };

  // 1) First pass: use explicit sprint.task_assignments where assignee_type/assignee_id are set
  assignments.forEach((a: any) => {
    const rawTaskId = a?.task_id ?? a?.taskId;
    if (rawTaskId == null) return;

    const ticketKey = String(rawTaskId);
    sprintTaskIds.add(ticketKey);

    const ticket = allTicketsById.get(ticketKey);
    if (!ticket) return;

    const hasAssignee = a.assignee_type && a.assignee_id != null;

    if (!hasAssignee) {
      // We'll handle fallback via ticket.assignees later
      return;
    }

    sprintAssignedTaskIds.add(ticketKey);

    const assigneeType: string = a.assignee_type;
    const assigneeId: number = Number(a.assignee_id);

    // --- User assignee ---
    if (assigneeType === 'user') {
      const u = allUsers.find((u) => u.id === assigneeId);
      const label =
        `${u?.first_name || ''} ${u?.last_name || ''}`.trim() ||
        u?.email_address ||
        `User #${assigneeId}`;

      const email = u?.email_address || undefined;

      let avatarUrl: string | null = null;
      if (u?.profile_picture) {
        avatarUrl =
          typeof u.profile_picture === 'string'
            ? u.profile_picture
            : (u.profile_picture as any)?.url || null;
      }

      const initials =
        (u?.first_name?.[0] || '') + (u?.last_name?.[0] || '') ||
        (u?.email_address?.[0] || '?');

      const key = `user:${assigneeId}`;
      const section = getOrCreateSection(key, {
        assignee_type: 'user',
        assignee_id: assigneeId,
        label,
        email,
        avatarUrl,
        initials,
      });

      section.tasks.push(ticket);
      return;
    }

    // --- Instance assignee ---
    if (assigneeType === 'instance') {
      let b =
        instances.find((b) => b.id === assigneeId) || null;

      if (!b && scopedInstance && scopedInstance.id === assigneeId) {
        b = {
          id: scopedInstance.id,
          displayName: scopedInstance.name,
          instanceUniqueId: scopedInstance.slug,
          email: '',
          hosting: null,
        };
      }

      const label = b?.displayName || `Instance #${assigneeId}`;
      const email = b?.email || undefined;

      let avatarUrl: string | null = null;
      if (b?.logoUrl) {
        avatarUrl =
          typeof b.logoUrl === 'string' ? b.logoUrl : null;
      }

      const initials = (b?.displayName?.[0] || 'I').toUpperCase();

      const key = `instance:${assigneeId}`;
      const section = getOrCreateSection(key, {
        assignee_type: 'instance',
        assignee_id: assigneeId,
        label,
        email,
        avatarUrl,
        initials,
      });

      section.tasks.push(ticket);
      return;
    }

    // Any other assignee_type is ignored for now
  });

  // 2) Second pass: fallback to ticket.assignees for tasks that are in this sprint
  //    but have no explicit sprint-level assignee.
  sprintTaskIds.forEach((ticketKey) => {
    const ticket = allTicketsById.get(ticketKey);
    if (!ticket) return;

    // If already handled via sprint assignment, skip
    if (sprintAssignedTaskIds.has(ticketKey)) return;

    const assignees =
      (((ticket as any).assignees || []) as Array<{ id: number; type: string }>) || [];

    const userAssignees = assignees.filter((a) => a.type === 'user');
    const instanceAssignees = assignees.filter((a) => a.type === 'instance');

    // If still truly unassigned, drop into "Unassigned" bucket
    if (userAssignees.length === 0 && instanceAssignees.length === 0) {
      const section = getOrCreateSection('unassigned', {
        assignee_type: 'user',
        assignee_id: -1,
        label: 'Unassigned tasks',
        initials: 'U',
        isUnassigned: true,
      });
      section.tasks.push(ticket);
      return;
    }

    // Group by user assignees
    userAssignees.forEach((ua) => {
      const u = allUsers.find((u) => u.id === ua.id);
      const label =
        `${u?.first_name || ''} ${u?.last_name || ''}`.trim() ||
        u?.email_address ||
        `User #${ua.id}`;

      const email = u?.email_address || undefined;

      let avatarUrl: string | null = null;
      if (u?.profile_picture) {
        avatarUrl =
          typeof u.profile_picture === 'string'
            ? u.profile_picture
            : (u.profile_picture as any)?.url || null;
      }

      const initials =
        (u?.first_name?.[0] || '') + (u?.last_name?.[0] || '') ||
        (u?.email_address?.[0] || '?');

      const key = `user:${ua.id}`;
      const section = getOrCreateSection(key, {
        assignee_type: 'user',
        assignee_id: ua.id,
        label,
        email,
        avatarUrl,
        initials,
      });

      section.tasks.push(ticket);
    });

    // Optionally group by instance assignees too
    instanceAssignees.forEach((ba) => {
      let b =
        instances.find((b) => b.id === ba.id) || null;

      if (!b && scopedInstance && scopedInstance.id === ba.id) {
        b = {
          id: scopedInstance.id,
          displayName: scopedInstance.name,
          instanceUniqueId: scopedInstance.slug,
          email: '',
          hosting: null,
        };
      }

      const label = b?.displayName || `Instance #${ba.id}`;
      const email = b?.email || undefined;

      let avatarUrl: string | null = null;
      if (b?.logoUrl) {
        avatarUrl =
          typeof b.logoUrl === 'string' ? b.logoUrl : null;
      }

      const initials = (b?.displayName?.[0] || 'I').toUpperCase();

      const key = `instance:${ba.id}`;
      const section = getOrCreateSection(key, {
        assignee_type: 'instance',
        assignee_id: ba.id,
        label,
        email,
        avatarUrl,
        initials,
      });

      section.tasks.push(ticket);
    });
  });

  const sections = Array.from(sectionsMap.values()).sort((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase())
  );

  const totalTasks =
    activeSprint!.tasks_summary?.total ??
    sections.reduce((sum, s) => sum + s.tasks.length, 0);
  const completedTasks =
    activeSprint!.tasks_summary?.completed ??
    sections.reduce(
      (sum, s) =>
        sum + s.tasks.filter((t) => isTicketClosed(t.task_status)).length,
      0
    );
  const overallPct =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <>
      <div className="sprint-view-header">
        <div className="sprint-view-title-block">
          <h2>{activeSprint!.name}</h2>
          <div className="sprint-view-dates">
            {activeSprint!.start_date} → {activeSprint!.end_date}
          </div>
          {!!activeSprint!.status && (
            <span className={`sprint-status-pill sprint-status-${activeSprint!.status}`}>
              {activeSprint!.status}
            </span>
          )}
        </div>

        <div className="sprint-view-overall">
          <div className="sprint-overall-label">Overall Progress</div>
          <div className="sprint-progress-bar">
            <div
              className="sprint-progress-fill"
              style={{ width: `${overallPct}%` }}
            />
          </div>
          <div className="sprint-overall-meta">
            <span>
              {completedTasks}/{totalTasks} tasks complete
            </span>
            <span className="sprint-overall-percent">{overallPct}%</span>
          </div>
        </div>
      </div>

      <div className="sprint-view-body">
        {sections.length === 0 && (
          <div className="empty-hint">
            No assigned tasks for this sprint yet.
          </div>
        )}

        {sections.map((sec) => {
          const total = sec.tasks.length;
          const done = sec.tasks.filter((t) => isTicketClosed(t.task_status)).length;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;

          return (
            <div key={sec.key} className="sprint-assignee-row">
              <div className="sprint-assignee-avatar">
                {sec.avatarUrl ? (
                  <img src={sec.avatarUrl} alt={sec.label} />
                ) : (
                  <div className="sprint-assignee-initials">
                    {sec.initials}
                  </div>
                )}
              </div>
              <div className="sprint-assignee-main">
                <div className="sprint-assignee-header">
                  <div className="sprint-assignee-name">
                    {sec.label}
                    <span className="sprint-assignee-type-pill">
                      {sec.isUnassigned
                        ? 'Unassigned'
                        : sec.assignee_type === 'user'
                        ? 'User'
                        : 'Business'}
                    </span>
                  </div>
                  {sec.email && (
                    <div className="sprint-assignee-email">{sec.email}</div>
                  )}
                  <div className="sprint-assignee-progress">
                    <div className="sprint-progress-bar sprint-progress-bar-sm">
                      <div
                        className="sprint-progress-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="sprint-assignee-progress-label">
                      {done}/{total} done ({pct}%)
                    </div>
                  </div>
                </div>

                <ul className="sprint-task-list">
                  {sec.tasks.map((t) => (
                    <li key={t.id} className="sprint-task-row">
                      <div className="sprint-task-title">
                        <span className="sprint-task-id">#{t.id}</span>
                        <span className="sprint-task-name">
                          {t.task_name}
                        </span>
                      </div>
                      <div className="sprint-task-meta">
                        <span
                          className={`sprint-pill sprint-pill-urgency-${t.task_urgency}`}
                        >
                          {t.task_urgency}
                        </span>
                        <span
                          className={`sprint-pill sprint-pill-status-${t.task_status}`}
                        >
                          {t.task_status}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
})()}


          </div>
        </div>
      )}

      {/* Ticket Modal */}
      {showModal && (
        <div className="ticket-modal-overlay" onClick={closeModal}>
          <div className="ticket-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={closeModal}>
              <i className="fa fa-times"></i>
            </button>
            {selectedTicketState === 'loading' ? (
              <div className="dashboard-message">
                <h3>Loading ticket</h3>
                <p>Fetching the latest ticket details from Elite Core.</p>
              </div>
            ) : null}
            {selectedTicketState === 'forbidden' ? (
              <div className="dashboard-message">
                <h3>Access denied</h3>
                <p>{selectedTicketError || 'You do not have access to this ticket.'}</p>
              </div>
            ) : null}
            {selectedTicketState === 'not-found' ? (
              <div className="dashboard-message">
                <h3>Ticket not found</h3>
                <p>{selectedTicketError || 'This ticket could not be found.'}</p>
              </div>
            ) : null}
            {selectedTicketState === 'unavailable' ? (
              <div className="dashboard-message">
                <h3>Ticket service unavailable</h3>
                <p>
                  {selectedTicketError ||
                    'The Command Centre ticket service has not been connected to Elite Core yet.'}
                </p>
              </div>
            ) : null}
            {selectedTicketState === 'error' ? (
              <div className="dashboard-message">
                <h3>Unable to load ticket</h3>
                <p>{selectedTicketError || 'Command Centre could not load this ticket.'}</p>
              </div>
            ) : null}
            {selectedTicketState === 'loaded' && selectedTicket ? (
              <TicketDetailsPanel
                ticket={selectedTicket}
                commandCentreAuth={commandCentreAuth}
                appLoading={appLoading}
                availableUsers={selectedInstanceUsers}
                entityDisplayData={conversationParticipants}
                onTicketActivity={handleTicketActivity}
                onTicketUpdated={setSelectedTicket}
              />
            ) : null}
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && canCreateUsers && (
        <div
          className="ticket-modal-overlay"
          onClick={() => setShowCreateUserModal(false)}
        >
          <div
            className="ticket-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>
              {isInternalUser
                ? 'Create Internal User'
                : `Create User for ${activeInstance?.displayName}`}
            </h2>

            {!hasSlug && (
              <label>
                <input
                  type="checkbox"
                  checked={isInternalUser}
                  onChange={() => setIsInternalUser(!isInternalUser)}
                />{' '}
                Internal core-command User
              </label>
            )}

            <input
              placeholder="First Name"
              value={newUserData.first_name}
              onChange={(e) =>
                setNewUserData({ ...newUserData, first_name: e.target.value })
              }
            />
            <input
              placeholder="Last Name"
              value={newUserData.last_name}
              onChange={(e) =>
                setNewUserData({ ...newUserData, last_name: e.target.value })
              }
            />
            <input
              placeholder="Email"
              value={newUserData.email_address}
              onChange={(e) =>
                setNewUserData({ ...newUserData, email_address: e.target.value })
              }
            />
            <select
              value={newUserData.user_role}
              onChange={(e) =>
                setNewUserData({ ...newUserData, user_role: e.target.value })
              }
            >
              {/* Slug mode expects only user/administrator */}
              <option value="user">User</option>
              <option value="administrator">Administrator</option>
            </select>

            <button onClick={handleCreateUser}>Create User</button>

            {!isInternalUser && (
              <>
                <h3>Existing Users</h3>
                <ul>
                  {selectedInstanceUsers.map((u) => (
                    <li key={u.id}>
                      {u.first_name} {u.last_name} ({u.email_address})
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Sprint Modal */}
      {false && showCreateSprintPanel && canSeeSprints && (
        <div
          className="ticket-modal-overlay"
          onClick={() => setShowCreateSprintPanel(false)}
        >
          <div
            className="ticket-modal-content sprint-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Create Sprint</h2>

            <input
              placeholder="Sprint name"
              value={newSprint.name}
              onChange={(e) =>
                setNewSprint((prev) => ({ ...prev, name: e.target.value }))
              }
            />
            <div className="sprint-date-row">
              <label>
                Start Date
                <input
                  type="date"
                  value={newSprint.start_date}
                  onChange={(e) =>
                    setNewSprint((prev) => ({ ...prev, start_date: e.target.value }))
                  }
                />
              </label>
              <label>
                End Date
                <input
                  type="date"
                  value={newSprint.end_date}
                  onChange={(e) =>
                    setNewSprint((prev) => ({ ...prev, end_date: e.target.value }))
                  }
                />
              </label>
            </div>

            <h3>Attach Tasks & Assign</h3>
            <div className="sprint-tasks-list">
              {openTicketsForSprints.length === 0 && (
                <div className="empty-hint">No open tickets to attach.</div>
              )}
              {openTicketsForSprints.map((t) => (
                <div className="sprint-task-select-row" key={t.id}>
                  <label className="sprint-multi-item">
                    <input
                      type="checkbox"
                      checked={newSprintSelectedTasks.includes(t.id)}
                      onChange={() => toggleSprintTaskSelected(t.id)}
                    />
                    <span>
                      #{t.id} — {t.task_name} [{t.task_urgency}]
                    </span>
                  </label>

                  <select
                    multiple
                    size={4}
                    value={
                      (newSprintAssignments[t.id] || []).map(
                        (a) => `${a.assignee_type}:${a.assignee_id}`
                      )
                    }
                    onChange={(e: any) =>
                      setAssignmentsForTaskFromMulti(
                        t.id,
                        e.target.selectedOptions
                      )
                    }
                  >
                    {/* Users */}
                    {assignableUsers.length > 0 && (
                      <optgroup label="Users">
                        {assignableUsers.map((u) => (
                          <option key={`user-${u.id}`} value={`user:${u.id}`}>
                            {u.first_name} {u.last_name} ({u.email_address})
                          </option>
                        ))}
                      </optgroup>
                    )}

                    {/* Businesses */}
                    {assignableInstances.length > 0 && (
                      <optgroup label="Businesses">
                        {assignableInstances.map((b) => (
                          <option key={`instance-${b.id}`} value={`instance:${b.id}`}>
                            {b.displayName} {b.email ? `(${b.email})` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>

                </div>
              ))}
            </div>

            <div className="sprint-modal-actions">
              <button onClick={() => setShowCreateSprintPanel(false)}>Cancel</button>
              <button onClick={handleCreateSprintSubmit}>Create Sprint</button>
            </div>
          </div>
        </div>
      )}

      {hostingModal.show && hostingModal.data && (
        <div
          className="ticket-modal-overlay"
          onClick={() => setHostingModal({ show: false, data: null })}
        >
          <div
            className="ticket-modal-content hosting-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Website Hosting Details</h2>
            <p>
              <strong>URL:</strong> {hostingModal.data.websiteUrl || 'Unavailable'}
            </p>
            <p>
              <strong>Status:</strong> {hostingModal.data.status || 'Unknown'}
            </p>
            <button
              onClick={() => setHostingModal({ show: false, data: null })}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Create Client Modal */}
      {showCreateInstanceModal && canCreateInstances && (
        <div
          className="ticket-modal-overlay"
          onClick={() => setShowCreateInstanceModal(false)}
        >
          <div
            className="ticket-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Create Business</h2>
            <Formik<CreateInstanceFormValues>
              initialValues={{
                instance_name: '',
                instance_email: '',
                instance_password: '',
                notify_emails: '',
              }}
              validationSchema={Yup.object({
                instance_name: Yup.string().required('Required'),
                instance_email: Yup.string().email('Invalid email').required('Required'),
                instance_password: Yup.string().min(6, 'Password too short').required('Required'),
              })}
              onSubmit={handleCreateInstance}
            >
              {({ isSubmitting }) => (
                <Form className="create-business-form">
                  <div>
                    <ErrorMessage name="instance_name" component="span" />
                    <Field type="text" name="instance_name" placeholder="Business Name" />
                  </div>
                  <div>
                    <ErrorMessage name="instance_email" component="span" />
                    <Field type="email" name="instance_email" placeholder="Business Email" />
                  </div>
                  <div>
                    <ErrorMessage name="instance_password" component="span" />
                    <Field type="password" name="instance_password" placeholder="Password" />
                  </div>
                  <div>
                    <Field
                      type="text"
                      name="notify_emails"
                      placeholder="Additional emails (comma separated)"
                    />
                  </div>
                  <button type="submit" disabled={isSubmitting}>
                    Create Business
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};

const CommandCenterHome: React.FC<{ commandCentreAuth: CommandCentreAuthContext | null; appLoading: any }> = (
  props
) => {
  return (
    <CommandCenterProvider>
      <div className="dashboard-container core-command-service-container" id="command-center">
        <CommandCenterContent {...props} />
      </div>
    </CommandCenterProvider>
  );
};

export default CommandCenterHome;



